import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Alert,
  Badge,
  Spinner,
  Table,
  Modal,
  InputGroup
} from 'react-bootstrap';
import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../auth/AuthContext';

const ReadAIAccountConfig = () => {
  const { currentUser, userProfile } = useAuth();
  const [accountConfig, setAccountConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });
  const [syncLogs, setSyncLogs] = useState([]);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    email: 'admin@npigovernance.co.za',
    gmailEnabled: true,
    syncFrequency: '15min',
    webhookEnabled: true,
    autoPublish: false,
    notifyOnNew: true,
    defaultVisibility: 'private'
  });

  useEffect(() => {
    if (currentUser && userProfile) {
      loadAccountConfig();
      loadSyncLogs();
    }
  }, [currentUser, userProfile]);

  const loadAccountConfig = async () => {
    try {
      setLoading(true);
      const configDoc = await getDoc(doc(db, 'readaiAccounts', currentUser.uid));

      if (configDoc.exists()) {
        const config = configDoc.data();
        setAccountConfig(config);
        setFormData({
          email: config.email || 'admin@npigovernance.co.za',
          gmailEnabled: config.gmailConfig?.enabled ?? true,
          syncFrequency: config.gmailConfig?.syncFrequency || '15min',
          webhookEnabled: config.webhookConfig?.enabled ?? true,
          autoPublish: config.preferences?.autoPublish ?? false,
          notifyOnNew: config.preferences?.notifyOnNew ?? true,
          defaultVisibility: config.preferences?.defaultVisibility || 'private'
        });
      } else {
        setAccountConfig(null);
      }
    } catch (error) {
      console.error('Error loading account config:', error);
      showAlert('Failed to load configuration', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const loadSyncLogs = async () => {
    try {
      const logsQuery = query(
        collection(db, 'integrationLogs'),
        where('service', 'in', ['readai', 'readai_gmail_poller']),
        orderBy('timestamp', 'desc'),
        limit(10)
      );

      const snapshot = await getDocs(logsQuery);
      const logs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate()
      }));

      setSyncLogs(logs);
    } catch (error) {
      console.error('Error loading sync logs:', error);
    }
  };

  const showAlert = (message, type) => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: '', type: '' }), 5000);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleLinkAccount = async () => {
    try {
      setSaving(true);

      const configData = {
        userId: currentUser.uid,
        email: formData.email,
        linkedAt: Timestamp.now(),
        gmailConfig: {
          enabled: formData.gmailEnabled,
          lastSyncAt: null,
          syncFrequency: formData.syncFrequency
        },
        webhookConfig: {
          enabled: formData.webhookEnabled,
          webhookUrl: 'https://us-central1-workplace-readiness-5ba15.cloudfunctions.net/api/readAIWebhook',
          secretKey: '' // Will be generated server-side
        },
        preferences: {
          autoPublish: formData.autoPublish,
          notifyOnNew: formData.notifyOnNew,
          defaultVisibility: formData.defaultVisibility
        },
        status: 'active',
        updatedAt: Timestamp.now()
      };

      await setDoc(doc(db, 'readaiAccounts', currentUser.uid), configData);

      showAlert('ReadAI account linked successfully!', 'success');
      loadAccountConfig();
    } catch (error) {
      console.error('Error linking account:', error);
      showAlert('Failed to link account', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateConfig = async () => {
    try {
      setSaving(true);

      const updateData = {
        gmailConfig: {
          enabled: formData.gmailEnabled,
          syncFrequency: formData.syncFrequency,
          lastSyncAt: accountConfig?.gmailConfig?.lastSyncAt || null
        },
        webhookConfig: {
          enabled: formData.webhookEnabled,
          webhookUrl: accountConfig?.webhookConfig?.webhookUrl || '',
          secretKey: accountConfig?.webhookConfig?.secretKey || ''
        },
        preferences: {
          autoPublish: formData.autoPublish,
          notifyOnNew: formData.notifyOnNew,
          defaultVisibility: formData.defaultVisibility
        },
        updatedAt: Timestamp.now()
      };

      await updateDoc(doc(db, 'readaiAccounts', currentUser.uid), updateData);

      showAlert('Configuration updated successfully!', 'success');
      loadAccountConfig();
    } catch (error) {
      console.error('Error updating config:', error);
      showAlert('Failed to update configuration', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setTesting(true);

      // Test webhook endpoint
      const response = await fetch(
        'https://us-central1-workplace-readiness-5ba15.cloudfunctions.net/api/readAIWebhookTest'
      );

      if (response.ok) {
        showAlert('Connection test successful!', 'success');
      } else {
        showAlert('Connection test failed', 'danger');
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      showAlert('Connection test failed', 'danger');
    } finally {
      setTesting(false);
    }
  };

  const handleDisconnectAccount = async () => {
    try {
      await deleteDoc(doc(db, 'readaiAccounts', currentUser.uid));
      setAccountConfig(null);
      setShowDisconnectModal(false);
      showAlert('Account disconnected successfully', 'success');
    } catch (error) {
      console.error('Error disconnecting account:', error);
      showAlert('Failed to disconnect account', 'danger');
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const badgeMap = {
      received: 'info',
      processing: 'warning',
      completed: 'success',
      failed: 'danger'
    };
    return <Badge bg={badgeMap[status] || 'secondary'}>{status}</Badge>;
  };

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-3">Loading configuration...</p>
      </Container>
    );
  }

  return (
    <Container fluid>
      {alert.show && (
        <Alert variant={alert.type} dismissible onClose={() => setAlert({ show: false })}>
          {alert.message}
        </Alert>
      )}

      <Row>
        <Col lg={8}>
          {/* Connection Status Card */}
          <Card className="mb-4">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">ReadAI Integration Status</h5>
              {accountConfig ? (
                <Badge bg="success">Connected</Badge>
              ) : (
                <Badge bg="secondary">Not Connected</Badge>
              )}
            </Card.Header>
            <Card.Body>
              {accountConfig ? (
                <div>
                  <Row className="mb-3">
                    <Col md={6}>
                      <strong>Email:</strong>
                      <div className="text-muted">{accountConfig.email}</div>
                    </Col>
                    <Col md={6}>
                      <strong>Linked At:</strong>
                      <div className="text-muted">{formatDate(accountConfig.linkedAt?.toDate())}</div>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={6}>
                      <strong>Gmail Sync:</strong>
                      <div>
                        {accountConfig.gmailConfig?.enabled ? (
                          <Badge bg="success">Enabled</Badge>
                        ) : (
                          <Badge bg="secondary">Disabled</Badge>
                        )}
                        {accountConfig.gmailConfig?.enabled && (
                          <div className="text-muted small">
                            Every {accountConfig.gmailConfig?.syncFrequency}
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col md={6}>
                      <strong>Webhook:</strong>
                      <div>
                        {accountConfig.webhookConfig?.enabled ? (
                          <Badge bg="success">Enabled</Badge>
                        ) : (
                          <Badge bg="secondary">Disabled</Badge>
                        )}
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <strong>Last Sync:</strong>
                      <div className="text-muted">
                        {formatDate(accountConfig.gmailConfig?.lastSyncAt?.toDate())}
                      </div>
                    </Col>
                    <Col md={6}>
                      <strong>Auto-Publish:</strong>
                      <div>
                        {accountConfig.preferences?.autoPublish ? (
                          <Badge bg="info">Yes</Badge>
                        ) : (
                          <Badge bg="secondary">No</Badge>
                        )}
                      </div>
                    </Col>
                  </Row>
                </div>
              ) : (
                <Alert variant="info">
                  No ReadAI account linked. Configure settings below to connect your account.
                </Alert>
              )}
            </Card.Body>
          </Card>

          {/* Configuration Form */}
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Configuration Settings</h5>
            </Card.Header>
            <Card.Body>
              <Form>
                <h6 className="mb-3">Account Settings</h6>
                <Form.Group className="mb-3">
                  <Form.Label>ReadAI Email Address</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    disabled={accountConfig !== null}
                    placeholder="your-email@gmail.com"
                  />
                  <Form.Text className="text-muted">
                    This is the email address associated with your ReadAI account.
                  </Form.Text>
                </Form.Group>

                <hr className="my-4" />

                <h6 className="mb-3">Gmail Integration</h6>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="gmailEnabled"
                    label="Enable Gmail Sync"
                    checked={formData.gmailEnabled}
                    onChange={handleInputChange}
                  />
                  <Form.Text className="text-muted">
                    Automatically fetch ReadAI meeting summaries from Gmail
                  </Form.Text>
                </Form.Group>

                {formData.gmailEnabled && (
                  <Form.Group className="mb-3">
                    <Form.Label>Sync Frequency</Form.Label>
                    <Form.Select
                      name="syncFrequency"
                      value={formData.syncFrequency}
                      onChange={handleInputChange}
                    >
                      <option value="15min">Every 15 minutes</option>
                      <option value="30min">Every 30 minutes</option>
                      <option value="hourly">Hourly</option>
                      <option value="daily">Daily</option>
                    </Form.Select>
                  </Form.Group>
                )}

                <hr className="my-4" />

                <h6 className="mb-3">Webhook Integration</h6>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="webhookEnabled"
                    label="Enable Webhook"
                    checked={formData.webhookEnabled}
                    onChange={handleInputChange}
                  />
                  <Form.Text className="text-muted">
                    Receive real-time notifications when meetings end
                  </Form.Text>
                </Form.Group>

                {formData.webhookEnabled && (
                  <Form.Group className="mb-3">
                    <Form.Label>Webhook URL</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="text"
                        value="https://us-central1-workplace-readiness-5ba15.cloudfunctions.net/api/readAIWebhook"
                        readOnly
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={() => {
                          navigator.clipboard.writeText(
                            'https://us-central1-workplace-readiness-5ba15.cloudfunctions.net/api/readAIWebhook'
                          );
                          showAlert('Webhook URL copied to clipboard', 'success');
                        }}
                      >
                        <i className="bi bi-clipboard"></i>
                      </Button>
                    </InputGroup>
                    <Form.Text className="text-muted">
                      Configure this URL in your ReadAI webhook settings
                    </Form.Text>
                  </Form.Group>
                )}

                <hr className="my-4" />

                <h6 className="mb-3">Resource Preferences</h6>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="autoPublish"
                    label="Auto-publish new resources"
                    checked={formData.autoPublish}
                    onChange={handleInputChange}
                  />
                  <Form.Text className="text-muted">
                    Automatically publish new session resources when received
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="notifyOnNew"
                    label="Notify me when new resources are available"
                    checked={formData.notifyOnNew}
                    onChange={handleInputChange}
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>Default Visibility</Form.Label>
                  <Form.Select
                    name="defaultVisibility"
                    value={formData.defaultVisibility}
                    onChange={handleInputChange}
                  >
                    <option value="private">Private (Only me)</option>
                    <option value="cohort">Cohort (Assigned cohorts)</option>
                    <option value="public">Public (All users)</option>
                  </Form.Select>
                </Form.Group>

                <div className="d-flex gap-2">
                  {accountConfig ? (
                    <>
                      <Button
                        variant="primary"
                        onClick={handleUpdateConfig}
                        disabled={saving}
                      >
                        {saving ? 'Saving...' : 'Save Changes'}
                      </Button>
                      <Button
                        variant="outline-danger"
                        onClick={() => setShowDisconnectModal(true)}
                      >
                        Disconnect Account
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="primary"
                      onClick={handleLinkAccount}
                      disabled={saving}
                    >
                      {saving ? 'Linking...' : 'Link ReadAI Account'}
                    </Button>
                  )}
                  <Button
                    variant="outline-secondary"
                    onClick={handleTestConnection}
                    disabled={testing}
                  >
                    {testing ? 'Testing...' : 'Test Connection'}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          {/* Quick Info Card */}
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Integration Guide</h5>
            </Card.Header>
            <Card.Body>
              <h6>How it works:</h6>
              <ol>
                <li>Link your ReadAI account (admin@npigovernance.co.za)</li>
                <li>Enable Gmail sync or webhook</li>
                <li>Meeting recordings and notes automatically appear in "Previous Session Resources"</li>
                <li>Publish resources to cohorts</li>
              </ol>
              <hr />
              <h6>Setup ReadAI Webhook:</h6>
              <ol>
                <li>Go to ReadAI settings</li>
                <li>Navigate to Integrations</li>
                <li>Add webhook URL from above</li>
                <li>Save configuration</li>
              </ol>
              <Alert variant="info" className="mt-3">
                <small>
                  <strong>Note:</strong> Gmail sync runs automatically. Webhook provides real-time updates.
                </small>
              </Alert>
            </Card.Body>
          </Card>

          {/* Recent Sync Logs */}
          <Card>
            <Card.Header>
              <h5 className="mb-0">Recent Sync Activity</h5>
            </Card.Header>
            <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {syncLogs.length === 0 ? (
                <p className="text-muted text-center">No sync activity yet</p>
              ) : (
                <Table size="sm" hover>
                  <tbody>
                    {syncLogs.map(log => (
                      <tr key={log.id}>
                        <td>
                          <div>
                            <small className="text-muted">
                              {formatDate(log.timestamp)}
                            </small>
                          </div>
                          <div>{log.event}</div>
                          {getStatusBadge(log.status)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
            <Card.Footer>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={loadSyncLogs}
                className="w-100"
              >
                <i className="bi bi-arrow-clockwise"></i> Refresh Logs
              </Button>
            </Card.Footer>
          </Card>
        </Col>
      </Row>

      {/* Disconnect Confirmation Modal */}
      <Modal show={showDisconnectModal} onHide={() => setShowDisconnectModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Disconnect ReadAI Account</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <strong>Warning:</strong> Disconnecting your ReadAI account will stop automatic syncing
            of meeting recordings and notes. Existing resources will not be deleted.
          </Alert>
          <p>Are you sure you want to disconnect your ReadAI account?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDisconnectModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDisconnectAccount}>
            Disconnect
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ReadAIAccountConfig;
